import type { QueryClient } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { Service } from "../service";

import { MUTATION_KEYS, QUERY_KEYS, UPDATE } from "./common";

import { combine, throwify } from "~/shared/fp";
import type { GetMutationOptions, InferMutationGenerics } from "~/shared/tanstack-query";

type UseUpdateGenerics = InferMutationGenerics<Service[typeof UPDATE]>;
type UseUpdateOptions = GetMutationOptions<UseUpdateGenerics>;

function useUpdate(
  service: Service,
  options?: Omit<UseUpdateOptions, "mutationKey" | "mutationFn">,
  queryClient?: QueryClient,
) {
  const fallbackQueryClient = useQueryClient();
  const queryClientToUse = queryClient ?? fallbackQueryClient;
  const { onSuccess } = options ?? {};

  return useMutation(
    {
      ...options,
      mutationKey: MUTATION_KEYS[UPDATE],
      mutationFn: async (params) => {
        return throwify(await service.update(params)());
      },
      onSuccess: combine([
        () =>
          void queryClientToUse.invalidateQueries({
            queryKey: QUERY_KEYS._def,
          }),
        onSuccess,
      ]),
    } as UseUpdateOptions,
    queryClient,
  );
}

export { useUpdate };
